import { useCallback, useEffect, useState, type ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

interface SwipeCarouselProps {
  children: ReactNode[];
  className?: string;
  autoplayMs?: number;
  showDots?: boolean;
  showProgress?: boolean;
  loop?: boolean;
}

export function SwipeCarousel({
  children,
  className = "",
  autoplayMs = 6000,
  showDots = true,
  showProgress = true,
  loop = true,
}: SwipeCarouselProps) {
  const [selected, setSelected] = useState(0);
  const [progress, setProgress] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop, align: "start", containScroll: "trimSnaps" },
    autoplayMs > 0 ? [Autoplay({ delay: autoplayMs, stopOnInteraction: true })] : [],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
    setProgress(0);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!autoplayMs || !showProgress) return;
    const step = 50;
    const timer = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + (100 / autoplayMs) * step));
    }, step);
    return () => clearInterval(timer);
  }, [autoplayMs, showProgress, selected]);

  if (!children.length) return null;

  return (
    <div className={className}>
      <div ref={emblaRef} className="overflow-hidden rounded-3xl">
        <div className="flex touch-pan-y">
          {children.map((child, i) => (
            <div key={i} className="min-w-0 flex-[0_0_100%] pl-0">
              {child}
            </div>
          ))}
        </div>
      </div>
      {(showDots || showProgress) && children.length > 1 && (
        <div className="mt-3 space-y-2">
          {showProgress && (
            <div className="h-0.5 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full bg-gradient-brand transition-[width] duration-75 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          {showDots && (
            <div className="flex justify-center gap-1.5">
              {children.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => emblaApi?.scrollTo(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === selected ? "w-5 bg-brand-pink nav-glow-active" : "w-1.5 bg-white/25"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
